import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CalendarDays, ExternalLink, MapPin, Ticket, Users } from 'lucide-react';
import { colors, spacing, typography, borderRadius, breakpoints, shadows } from '../../styles/theme';
import type { Event } from '../../types/api';
import {
  getEventAddress,
  getEventDateTimeDisplay,
  getEventLocationLabel,
  getVenueLabel,
  truncate,
} from '../../utils/eventPresentation';
import { cleanStringArray, formatAudienceLevel, formatPrice, formatSpeaker } from '../../utils/eventFields';
import { formatEventLocalDateTime } from '../../utils/eventDateTime';
import { buildEventPath } from '../../utils/slug';
import { toAbsoluteUrl } from '../../utils/externalUrl';
import { EventBadges } from './EventBadges';
import EventEntityAvatars from './EventEntityAvatars';
import { useAnalytics } from '../../hooks/useAnalytics';
import RememberButton from './RememberButton';

/**
 * The canonical event row. Desktop renders a date rail, a flexible
 * title/metadata column, a wrapping badge area, and an actions column; mobile
 * stacks the same content vertically with 44px touch targets and no horizontal
 * overflow.
 */

const Row = styled.article.withConfig({
  shouldForwardProp: (prop) => prop !== 'isHighlighted' && prop !== 'isPast',
})<{ isHighlighted?: boolean; isPast?: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: 132px minmax(0, 1fr) auto;
  gap: ${spacing[4]};
  align-items: start;
  padding: ${spacing[4]};
  background: ${colors.white};
  border: 1px solid ${(props) => (props.isHighlighted ? colors.primary[400] : colors.border.light)};
  border-left: 4px solid ${(props) => (props.isHighlighted ? colors.primary[500] : 'transparent')};
  border-radius: ${borderRadius.lg};
  cursor: pointer;
  opacity: ${(props) => (props.isPast ? 0.82 : 1)};
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: ${colors.primary[300]};
    box-shadow: ${shadows.md};
  }

  &:focus-within {
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px ${colors.primary[100]};
  }

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: minmax(0, 1fr);
    gap: ${spacing[3]};
    padding: ${spacing[3]};
  }
`;

const DateRail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-right: ${spacing[3]};
  border-right: 1px solid ${colors.border.light};
  min-width: 0;

  @media (max-width: ${breakpoints.md}) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: baseline;
    gap: ${spacing[2]};
    padding-right: 0;
    border-right: none;
  }
`;

const DateText = styled.time`
  display: block;
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
`;

const WeekdayText = styled.span`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const TimeText = styled.span`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.secondary};
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  min-width: 0;
`;

const TitleLink = styled.a`
  font-size: ${typography.fontSizes.lg};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  text-decoration: none;
  overflow-wrap: anywhere;

  &:hover,
  &:focus-visible {
    color: ${colors.primary[600]};
    text-decoration: underline;
  }
`;

const Summary = styled.p`
  margin: 0;
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.secondary};
  line-height: ${typography.lineHeights.normal};
  overflow-wrap: anywhere;
`;

const MetaList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[1]} ${spacing[3]};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.secondary};
  min-width: 0;
`;

const MetaItem = styled.li`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  min-width: 0;
  overflow-wrap: anywhere;

  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    color: ${colors.text.tertiary};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${spacing[2]};
  min-width: 0;

  @media (max-width: ${breakpoints.md}) {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const ActionLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[1]};
  min-height: 44px;
  padding: 0 ${spacing[3]};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.primary[300]};
  background: ${colors.primary[50]};
  color: ${colors.primary[700]};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  text-decoration: none;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    background: ${colors.primary[100]};
  }
`;

const SecondaryActionLink = styled(ActionLink)`
  border-color: ${colors.border.normal};
  background: ${colors.white};
  color: ${colors.text.secondary};

  &:hover,
  &:focus-visible {
    background: ${colors.gray[100]};
  }
`;

const DistanceText = styled.span`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.tertiary};
  text-align: center;
`;

export interface EventListRowProps {
  event: Event;
  /** Entity whose page this is, so its own relationship badge stays bare. */
  contextEntityId?: number | null;
  isHighlighted?: boolean;
  isPast?: boolean;
  onHoverChange?: (eventId: number | null) => void;
  onSelect?: (event: Event) => void;
}

/** Stop a click on an inner control from also navigating the row. */
function stopRowNavigation(e: React.MouseEvent) {
  e.stopPropagation();
}

export const EventListRow = React.forwardRef<HTMLElement, EventListRowProps>(function EventListRow(
  { event, contextEntityId, isHighlighted, isPast, onHoverChange, onSelect },
  ref,
) {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const when = getEventDateTimeDisplay(event);
  const path = buildEventPath(event);
  const venue = getVenueLabel(event);
  const address = getEventAddress(event);
  const location = getEventLocationLabel(event);
  const price = formatPrice(event.price, event.currency, event.isFree);
  const audience = formatAudienceLevel(event.audienceLevel);
  const topics = cleanStringArray(event.topics);
  const industries = cleanStringArray(event.industries);
  const speakers = Array.isArray(event.speakers) ? event.speakers.map(formatSpeaker).filter(Boolean) : [];
  const deadline = formatEventLocalDateTime(event.registrationDeadline);
  const description = truncate(event.description, 220);
  const sourceLinks = event.eventSources ?? [];
  const registrationUrl = toAbsoluteUrl(event.registrationUrl, [event.eventUrl, ...sourceLinks]);
  const externalEventUrl = toAbsoluteUrl(event.eventUrl, sourceLinks);

  const openDetail = useCallback(() => {
    onSelect?.(event);
    if (path) navigate(path);
  }, [event, navigate, onSelect, path]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDetail();
    }
  };

  return (
    <Row
      ref={ref as React.Ref<HTMLElement>}
      data-event-id={event.eventId}
      isHighlighted={isHighlighted}
      isPast={isPast}
      onClick={openDetail}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => onHoverChange?.(event.eventId)}
      onMouseLeave={() => onHoverChange?.(null)}
      onFocus={() => onHoverChange?.(event.eventId)}
      tabIndex={0}
      role="listitem"
      aria-label={`${event.title}${when.dateLabel ? `, ${when.dateLabel}` : ''}`}
    >
      <DateRail>
        {when.weekday && <WeekdayText>{when.weekday}</WeekdayText>}
        {when.dateLabel && <DateText dateTime={when.startIso}>{when.dateLabel}</DateText>}
        {when.timeLabel && <TimeText>{when.timeLabel}</TimeText>}
        {when.duration && <TimeText>{when.duration}</TimeText>}
        {typeof event.distanceKm === 'number' && (
          <DistanceText>{`${event.distanceKm.toFixed(1)} km away`}</DistanceText>
        )}
      </DateRail>

      <Body>
        <TitleLink
          href={path || undefined}
          onClick={(e) => {
            // Let modified clicks (new tab/window) behave natively.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            e.stopPropagation();
            openDetail();
          }}
        >
          {event.title}
        </TitleLink>

        {description && <Summary>{description}</Summary>}

        <MetaList>
          {venue && (
            <MetaItem>
              <MapPin aria-hidden="true" />
              <span>{venue}</span>
            </MetaItem>
          )}
          {address && address !== venue && (
            <MetaItem>
              <span>{address}</span>
            </MetaItem>
          )}
          {location && location !== address && (
            <MetaItem>
              <span>{location}</span>
            </MetaItem>
          )}
          {event.type && (
            <MetaItem>
              <CalendarDays aria-hidden="true" />
              <span>{event.type}</span>
            </MetaItem>
          )}
          {audience && (
            <MetaItem>
              <Users aria-hidden="true" />
              <span>{audience}</span>
            </MetaItem>
          )}
          {price && (
            <MetaItem>
              <Ticket aria-hidden="true" />
              <span>{price}</span>
            </MetaItem>
          )}
          {deadline && (
            <MetaItem>
              <span>{`Register by ${deadline}`}</span>
            </MetaItem>
          )}
          {typeof event.ticketsRemaining === 'number' && (
            <MetaItem>
              <span>{`${event.ticketsRemaining.toLocaleString()} tickets left`}</span>
            </MetaItem>
          )}
          {speakers.length > 0 && (
            <MetaItem>
              <span>{`Speakers: ${speakers.slice(0, 3).join(', ')}${speakers.length > 3 ? ` +${speakers.length - 3}` : ''}`}</span>
            </MetaItem>
          )}
          {topics.length > 0 && (
            <MetaItem>
              <span>{`Topics: ${topics.slice(0, 4).join(', ')}`}</span>
            </MetaItem>
          )}
          {industries.length > 0 && (
            <MetaItem>
              <span>{`Industries: ${industries.slice(0, 3).join(', ')}`}</span>
            </MetaItem>
          )}
        </MetaList>

        <EventEntityAvatars entities={event.entities} />
        <EventBadges event={event} contextEntityId={contextEntityId} />
      </Body>

      <Actions onClick={stopRowNavigation}>
        <RememberButton eventId={event.eventId} actionLocation="event_list_row" />
        {registrationUrl && (
          <ActionLink
            href={registrationUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent({ category: 'event_detail', action: 'registration_click', custom_parameters: { action_location: 'event_list_row', source: 'canonical_registration' } })}
          >
            Register
            <ExternalLink aria-hidden="true" size={14} />
          </ActionLink>
        )}
        {externalEventUrl && (
          <SecondaryActionLink
            href={externalEventUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent({ category: 'event_detail', action: 'source_click', custom_parameters: { action_location: 'event_list_row', source: 'canonical_event_page' } })}
          >
            Source
            <ExternalLink aria-hidden="true" size={14} />
          </SecondaryActionLink>
        )}
      </Actions>
    </Row>
  );
});

export default EventListRow;
